import { FormEvent, useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Clock,
  Link as LinkIcon,
  Lock,
  LogIn,
  Mail,
  Sparkles
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../auth/AuthProvider';
import { EMAIL_REDIRECT_TO } from '../lib/authRedirect';

const toFlag = (value?: string) => (value ?? '').toLowerCase() === 'true';

const ENABLE_MAGIC_LINK = toFlag(process.env.REACT_APP_AUTH_ENABLE_MAGIC_LINK);
const PASSWORD_RESET_SUCCESS_KEY = 'supabase.password-reset-success';

const Login = () => {
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [msgType, setMsgType] = useState<'success' | 'error' | 'info' | null>(null);
  const { session } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const redirectAfterAuth = useCallback(() => {
    const fromState = (location.state as { from?: { pathname?: string } } | null)?.from;
    const redirectTo = fromState?.pathname ?? '/';
    navigate(redirectTo, { replace: true });
  }, [location.state, navigate]);

  useEffect(() => {
    if (session) {
      redirectAfterAuth();
    }
  }, [session, redirectAfterAuth]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const resetCompleted = window.sessionStorage.getItem(PASSWORD_RESET_SUCCESS_KEY);
    if (resetCompleted) {
      setMsg('パスワードを更新しました。新しいパスワードでログインしてください。');
      setMsgType('success');
      window.sessionStorage.removeItem(PASSWORD_RESET_SUCCESS_KEY);
    }
  }, []);

  const ensureClient = useCallback((): boolean => {
    if (!supabase) {
      setMsg('Supabase クライアントが初期化されていません。環境変数を確認してください。');
      setMsgType('error');
      return false;
    }
    return true;
  }, []);

  const signInWithPassword = async (e: FormEvent) => {
    e.preventDefault();
    if (!ensureClient()) return;
    setMsg(null);
    setMsgType(null);
    setSubmitting(true);
    try {
      const { error } = await supabase!.auth.signInWithPassword({ email, password: pw });
      const translated =
        error?.message === 'Invalid login credentials'
          ? 'メールアドレスまたはパスワードが正しくありません。'
          : error?.message;
      setMsg(translated ?? 'サインイン成功！');
      setMsgType(error ? 'error' : 'success');
      if (!error) {
        redirectAfterAuth();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const magicLink = async () => {
    if (!ENABLE_MAGIC_LINK) {
      setMsg('現在、マジックリンクは無効化されています。管理者にお問い合わせください。');
      setMsgType('info');
      return;
    }
    if (!ensureClient()) return;
    setMsg(null);
    setMsgType(null);
    setSubmitting(true);
    try {
      const { error } = await supabase!.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: EMAIL_REDIRECT_TO }
      });
      setMsg(error ? error.message : 'マジックリンクを送信しました');
      setMsgType(error ? 'error' : 'success');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-br from-blue-50 via-white to-blue-100">
      <header className="bg-gray-800 px-6 py-4 text-white shadow">
        <div className="mx-auto flex w-full max-w-6xl items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-blue-500 shadow-inner">
              <Clock className="h-5 w-5" />
            </div>
            <div>
              <p className="text-lg font-semibold tracking-wide">勤怠管理システム</p>
            </div>
          </div>
          <span className="hidden text-sm text-blue-100 md:inline">
            管理者ポータルへサインインしてください
          </span>
        </div>
      </header>

      <main className="flex flex-1 items-center justify-center px-6 py-12">
        <div className="w-full max-w-6xl">
          <div className="grid gap-8 rounded-3xl bg-white/70 p-8 shadow-2xl backdrop-blur md:grid-cols-[1fr_1fr] lg:p-12">
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-600 via-blue-500 to-sky-500 p-8 text-white shadow-xl">
              <div className="absolute -left-8 -top-8 h-40 w-40 rounded-full bg-white/10 blur-2xl" />
              <div className="absolute -bottom-12 -right-10 h-44 w-44 rounded-full bg-white/10 blur-2xl" />
              <div className="relative flex h-full flex-col justify-between gap-10">
                <div className="space-y-4 text-center md:text-left">
                  <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-2 text-xs font-medium uppercase tracking-[0.2em] text-blue-50">
                    <Sparkles className="h-4 w-4" />
                    Welcome Back
                  </span>
                  <h2 className="text-3xl font-semibold leading-tight md:text-4xl">
                    勤怠データ管理アプリ
                  </h2>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-center">
              <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-xl ring-1 ring-gray-100">
                <div className="mb-8 text-center">
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-blue-100 text-blue-600">
                    <LogIn className="h-6 w-6" />
                  </div>
                  <h1 className="mt-4 text-2xl font-semibold text-gray-900">管理者ログイン</h1>
                  <p className="mt-2 text-sm text-gray-500">
                    登録済みのメールアドレスとパスワードでサインインしてください。
                  </p>
                </div>

                {msg && (
                  <div
                    className={[
                      'mb-6 rounded-lg border px-4 py-3 text-sm',
                      msgType === 'error'
                        ? 'border-red-200 bg-red-50 text-red-700'
                        : msgType === 'success'
                          ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                          : 'border-blue-200 bg-blue-50 text-blue-700'
                    ].join(' ')}
                  >
                    {msg}
                  </div>
                )}

                <form onSubmit={signInWithPassword} className="space-y-5">
                  <label className="block text-left">
                    <span className="text-sm font-medium text-gray-700">メールアドレス</span>
                    <div className="relative mt-2">
                      <Mail className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                      <input
                        placeholder="email@example.com"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        disabled={submitting}
                        className="w-full rounded-lg border border-gray-200 bg-gray-50 px-10 py-3 text-gray-900 shadow-sm transition focus:border-blue-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:cursor-not-allowed disabled:bg-gray-100"
                        type="email"
                        autoComplete="email"
                        required
                      />
                    </div>
                  </label>

                  <label className="block text-left">
                    <span className="text-sm font-medium text-gray-700">パスワード</span>
                    <div className="relative mt-2">
                      <Lock className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
                      <input
                        placeholder="••••••••"
                        type="password"
                        value={pw}
                        onChange={e => setPw(e.target.value)}
                        disabled={submitting}
                        className="w-full rounded-lg border border-gray-200 bg-gray-50 px-10 py-3 text-gray-900 shadow-sm transition focus:border-blue-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:cursor-not-allowed disabled:bg-gray-100"
                        autoComplete="current-password"
                        required
                      />
                    </div>
                  </label>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white shadow-md transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:cursor-not-allowed disabled:bg-blue-300"
                  >
                    <LogIn className="h-4 w-4" />
                    メール/パスワードでログイン
                  </button>
                  <div className="mt-3 text-right text-xs">
                    <button
                      type="button"
                      onClick={() => navigate('/password-reset')}
                      className="text-blue-600 underline underline-offset-2 hover:text-blue-700"
                    >
                      パスワードをお忘れですか？
                    </button>
                  </div>
                </form>

                <div className="mt-8 space-y-3">
                  {ENABLE_MAGIC_LINK && (
                    <button
                      type="button"
                      onClick={magicLink}
                      disabled={submitting}
                      className="flex w-full items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm font-semibold text-gray-700 transition hover:border-blue-300 hover:text-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-200 disabled:cursor-not-allowed"
                    >
                      <LinkIcon className="h-4 w-4" />
                      マジックリンクを受け取る
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Login;
